/**
 * Office RADIO — the client YouTube IFrame player (ADR-0021, emotion: Belonging).
 *
 * The visual/audio half of the host-controlled radio. The realtime server is authoritative over the
 * shared *state* (videoId / position / play-pause / queue); this module runs ONE YouTube IFrame on each
 * client and reconciles it toward that state. We deliberately run each client's OWN embed (never proxy
 * audio) because YouTube's ToS forbids extracting/proxying the stream AND forbids hidden / audio-only /
 * background playback — so the player is kept VISIBLE (a small Now-Playing card; OfficePage mounts it)
 * with the native controls intact. The structural cousin of {@link import('../sound/soundboard-audio')}:
 * SSR-safe, one lazily-created shared instance, a dev `radioDebug()` snapshot, every failure a silent
 * no-op. Nothing is persisted (Constitution Art. 1 — shared ambient delight, never surveillance).
 *
 * Sync model (matches the Watch2Gether / Discord / PubNub prior art): the server stamps an anchor
 * {positionMs, updatedAt, epoch}; the client extrapolates the expected position locally and only
 * `seekTo`s when it has drifted past {@link DRIFT_THRESHOLD_S} (≈ the human-imperceptible band) OR the
 * `epoch` bumped on a genuine DJ action. Position never rides React state (no 20 Hz re-renders).
 */

/** Re-seek only when the local player has drifted more than this from the extrapolated target (seconds).
 *  ~1.2s sits inside the ±0.5–2s human-imperceptible band for music and avoids audible micro-seek stutter. */
const DRIFT_THRESHOLD_S = 1.2;

/** The official IFrame Player API loader (served from youtube.com; the embed itself uses the nocookie host). */
const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

// ── Minimal structural types for the YouTube IFrame Player API (avoids a @types/youtube dependency). ──
interface YTPlayer {
  loadVideoById(id: string, startSeconds?: number): void;
  cueVideoById(id: string, startSeconds?: number): void;
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getPlayerState(): number;
  setVolume(volume: number): void;
  mute(): void;
  unMute(): void;
  destroy(): void;
}
interface YTStateChangeEvent {
  data: number;
}
interface YTErrorEvent {
  data: number;
}
type YTPlayerCtor = new (el: string | HTMLElement, opts: Record<string, unknown>) => YTPlayer;
interface YTNamespace {
  Player: YTPlayerCtor;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}
declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/** The shared playback state + local prefs the reconciler needs each call (mirrors the server anchor). */
export interface RadioApplyInput {
  /** Current YouTube videoId, or '' when the radio is idle. */
  videoId: string;
  /** Whether the server says playback is running. */
  playing: boolean;
  /** Server-authoritative position (ms) AS OF `updatedAt`. */
  positionMs: number;
  /** Server wall-clock (ms) when `positionMs` was stamped. */
  updatedAt: number;
  /** Bumps on a genuine DJ action → force a hard seek; unchanged through heartbeat re-stamps. */
  epoch: number;
  /** Estimated server-clock offset (ms): serverNow ≈ Date.now() + this. From the RADIO_CLOCK probe. */
  clockOffsetMs: number;
  /** This listener's local volume (0..100) — personal, never shared. */
  volume: number;
  /** This listener's personal mute. */
  muted: boolean;
  /** Whether the listener has tapped "join the radio" (autoplay-with-sound needs a user gesture). */
  unlocked: boolean;
}

/** Lifecycle callbacks OfficePage wires up (e.g. the OWNER's client reports ENDED/onError as a skip). */
export interface RadioPlayerCallbacks {
  /** The current track reached its end — the DJ's client advances the queue (others just follow state). */
  onEnded?: () => void;
  /** The current track is unplayable here (embed disabled 101/150, removed/private 100, bad id 2). */
  onError?: (code: number) => void;
  /** The player finished loading and is ready for method calls. */
  onReady?: () => void;
  /** Coarse play/pause transitions, for optional UI (true = playing). */
  onPlayingChange?: (playing: boolean) => void;
}

let player: YTPlayer | null = null;
let ready = false;
let apiReady: Promise<void> | null = null;
let callbacks: RadioPlayerCallbacks = {};
/** The videoId currently loaded into the IFrame (vs the desired one from server state). */
let currentVideoId = '';
/** The last epoch we reconciled — a change forces a hard seek. */
let currentEpoch = -1;
/** The most recent desired state, so a user-gesture unlock can start playback synchronously. */
let lastInput: RadioApplyInput | null = null;
/**
 * Whether THIS client has had a real user gesture yet (tap-to-listen, or a click on the native YouTube
 * play button). Audio-unlock is required by the browser before we may `playVideo()`; once true we may
 * re-assert the host's state even if the listener used the native controls instead of our button.
 */
let userGestured = false;

// ── Dev/e2e observability (read by OfficePage's `window.__duckRadio()` hook). ──
let appliedCount = 0;
let seekCount = 0;

/** Expected playback position (seconds) extrapolated from the server anchor + the local clock offset. */
function expectedSeconds(input: RadioApplyInput): number {
  const elapsed = input.playing ? Date.now() + input.clockOffsetMs - input.updatedAt : 0;
  return Math.max(0, (input.positionMs + elapsed) / 1000);
}

function playerStateConst(name: keyof YTNamespace['PlayerState']): number {
  return window.YT?.PlayerState?.[name] ?? -99;
}

/** Load the IFrame Player API script once and resolve when `window.YT` is ready (no-op during SSR). */
function loadApi(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();
  if (apiReady) return apiReady;
  apiReady = new Promise<void>((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = IFRAME_API_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return apiReady;
}

/** Apply the local volume/mute to the live player (safe to call repeatedly). */
function applyVolume(input: RadioApplyInput): void {
  const p = player;
  if (!p || !ready) return;
  try {
    if (input.muted || input.volume <= 0) {
      p.mute();
    } else {
      p.unMute();
      p.setVolume(Math.max(0, Math.min(100, Math.round(input.volume))));
    }
  } catch {
    /* player not ready for method calls yet — ignored */
  }
}

/**
 * Re-assert the HOST's authoritative play/pause onto the local player (ADR-0021). Only the DJ changes
 * playback (via the card → server → everyone); the native YouTube controls are therefore DECORATIVE for
 * every client — if anyone pauses/plays locally, we immediately snap back to the synced state. This is
 * ToS-safe: we never hide, disable, or overlay YouTube's controls (which the ToS forbids), we just
 * counter a local change by re-applying the server's truth. Play is gated on a prior user gesture so we
 * never trip the autoplay-with-sound block (a never-interacted listener stays cued/silent until they tap).
 */
function enforceServerState(): void {
  const p = player;
  const input = lastInput;
  if (!p || !ready || !input || !input.videoId) return;
  let state = -1;
  try {
    state = p.getPlayerState();
  } catch {
    return;
  }
  const PLAYING = playerStateConst('PLAYING');
  const BUFFERING = playerStateConst('BUFFERING');
  if (input.playing) {
    // Host says PLAYING → counter any local pause (only once audio is unlocked, else autoplay blocks).
    if ((input.unlocked || userGestured) && state !== PLAYING && state !== BUFFERING) {
      try {
        p.playVideo();
      } catch {
        /* ignored */
      }
    }
  } else if (state === PLAYING) {
    // Host says PAUSED → counter any local play.
    try {
      p.pauseVideo();
    } catch {
      /* ignored */
    }
  }
}

/**
 * Create (once) the shared YouTube IFrame inside the DOM node with id `elementId`. The node is replaced
 * by the iframe, so OfficePage mounts a dedicated, React-untouched child div for it. Idempotent: a second
 * call is a no-op while a player exists. The player is created paused/cued — playback only starts after a
 * user gesture (see {@link unlockRadio}), so we never trip the browser's autoplay-with-sound block.
 */
export function ensureRadioPlayer(elementId: string, cb: RadioPlayerCallbacks): void {
  callbacks = cb;
  if (player || typeof window === 'undefined') return;
  void loadApi().then(() => {
    if (player) return; // a concurrent call already created it
    const YT = window.YT;
    if (!YT?.Player || !document.getElementById(elementId)) return;
    try {
      player = new YT.Player(elementId, {
        host: 'https://www.youtube-nocookie.com', // privacy-enhanced embed (aligns with the no-surveillance charter + CSP)
        width: '100%',
        height: '100%',
        playerVars: {
          controls: 1, // native controls kept (ToS — never hide/replace them)
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: () => {
            ready = true;
            if (lastInput) {
              applyVolume(lastInput);
              applyRadioState(lastInput);
            }
            callbacks.onReady?.();
          },
          onStateChange: (e: YTStateChangeEvent) => {
            const PLAYING = playerStateConst('PLAYING');
            // Any successful PLAYING transition (our reconciler OR the user's native play button) means
            // this client's audio is now unlocked — record it so we may re-assert the host's state.
            if (e.data === PLAYING) userGestured = true;
            if (e.data === playerStateConst('ENDED')) callbacks.onEnded?.();
            callbacks.onPlayingChange?.(e.data === PLAYING);
            // Someone poked the native controls — snap playback back to what the DJ dictates (the native
            // bar is decorative; only the host changes playback for the room).
            if (e.data === PLAYING || e.data === playerStateConst('PAUSED')) enforceServerState();
          },
          onError: (e: YTErrorEvent) => {
            callbacks.onError?.(e.data);
          },
        },
      });
    } catch {
      /* IFrame API not available — radio degrades to "no audio", never throws */
    }
  });
}

/**
 * Reconcile the local player toward the shared server state. Called on every radio-state change + a low
 * cadence (so wall-clock-driven drift is corrected). Loads the right track, applies play/pause, corrects
 * drift past {@link DRIFT_THRESHOLD_S}, and forces a hard seek on an `epoch` bump. All player calls are
 * guarded — a not-yet-ready player just no-ops (and re-applies on its `onReady`). Audio only starts when
 * `unlocked` is true (the user tapped to join); until then we `cueVideoById` (loaded but silent).
 */
export function applyRadioState(input: RadioApplyInput): void {
  lastInput = input;
  appliedCount += 1;
  const p = player;
  if (!p || !ready) return;

  applyVolume(input);

  // Idle: nothing should be loaded → stop whatever was playing.
  if (!input.videoId) {
    if (currentVideoId) {
      try {
        p.stopVideo();
      } catch {
        /* ignored */
      }
      currentVideoId = '';
      currentEpoch = input.epoch;
    }
    return;
  }

  // Track change: load (autoplay if unlocked + playing) or cue (silent until the user joins / it plays).
  if (input.videoId !== currentVideoId) {
    currentVideoId = input.videoId;
    currentEpoch = input.epoch;
    const start = expectedSeconds(input);
    try {
      if (input.playing && input.unlocked) p.loadVideoById(input.videoId, start);
      else p.cueVideoById(input.videoId, start);
    } catch {
      /* ignored */
    }
    return;
  }

  const epochChanged = input.epoch !== currentEpoch;
  currentEpoch = input.epoch;

  let state = -1;
  try {
    state = p.getPlayerState();
  } catch {
    return; // not ready for method calls
  }
  const BUFFERING = playerStateConst('BUFFERING');

  // Apply the host's play/pause (re-asserts over any local control — see enforceServerState).
  enforceServerState();

  // Drift correction: hard-seek on an epoch bump (a real DJ action); otherwise nudge only past threshold.
  if ((input.unlocked || userGestured) && state !== BUFFERING) {
    let cur = 0;
    try {
      cur = p.getCurrentTime();
    } catch {
      return;
    }
    const expected = expectedSeconds(input);
    if (epochChanged || (input.playing && Math.abs(cur - expected) > DRIFT_THRESHOLD_S)) {
      try {
        p.seekTo(expected, true);
        seekCount += 1;
      } catch {
        /* ignored */
      }
    }
  }
}

/**
 * The "tap to join the radio" gesture (autoplay-with-sound policy). Call this SYNCHRONOUSLY from a real
 * user click so the browser allows audio: it unmutes, sets the stored volume, and starts playback from
 * the extrapolated position. OfficePage also flips its `radioUnlocked` flag so the steady-state reconciler
 * takes over afterward. A no-op until the player is ready / when there's nothing to play.
 */
export function unlockRadio(): void {
  userGestured = true; // this tap is the audio-unlock gesture; future re-asserts may now playVideo()
  const p = player;
  if (!p || !ready || !lastInput) return;
  try {
    if (!lastInput.muted) {
      p.unMute();
      p.setVolume(Math.max(0, Math.min(100, Math.round(lastInput.volume))));
    }
    if (lastInput.videoId && lastInput.playing) {
      const start = expectedSeconds(lastInput);
      if (lastInput.videoId !== currentVideoId) {
        currentVideoId = lastInput.videoId;
        currentEpoch = lastInput.epoch;
        p.loadVideoById(lastInput.videoId, start);
      } else {
        p.seekTo(start, true);
        p.playVideo();
      }
    }
  } catch {
    /* ignored */
  }
}

/** Immediately apply a volume change (0..100) from the slider, without waiting for the next reconcile. */
export function setRadioVolume(volume: number): void {
  const p = player;
  if (!p || !ready) return;
  const v = Math.max(0, Math.min(100, Math.round(volume)));
  try {
    if (v <= 0) p.mute();
    else {
      p.unMute();
      p.setVolume(v);
    }
  } catch {
    /* ignored */
  }
}

/** Immediately apply a mute toggle from the button, without waiting for the next reconcile. */
export function setRadioMuted(muted: boolean): void {
  const p = player;
  if (!p || !ready) return;
  try {
    if (muted) p.mute();
    else {
      p.unMute();
      if (lastInput) p.setVolume(Math.max(0, Math.min(100, Math.round(lastInput.volume))));
    }
  } catch {
    /* ignored */
  }
}

/** Tear the player down on unmount (leaving the office). Lets a later mount recreate it cleanly. */
export function destroyRadioPlayer(): void {
  const p = player;
  player = null;
  ready = false;
  currentVideoId = '';
  currentEpoch = -1;
  lastInput = null;
  userGestured = false;
  callbacks = {};
  if (p) {
    try {
      p.destroy();
    } catch {
      /* ignored */
    }
  }
}

/** Dev/e2e snapshot of the radio player (drives `window.__duckRadio()`). */
export function radioDebug(): {
  ready: boolean;
  currentVideoId: string;
  currentEpoch: number;
  playerState: number;
  currentTimeMs: number;
  appliedCount: number;
  seekCount: number;
} {
  let playerState = -1;
  let currentTimeMs = 0;
  if (player && ready) {
    try {
      playerState = player.getPlayerState();
      currentTimeMs = Math.round(player.getCurrentTime() * 1000);
    } catch {
      /* ignored */
    }
  }
  return {
    ready,
    currentVideoId,
    currentEpoch,
    playerState,
    currentTimeMs,
    appliedCount,
    seekCount,
  };
}
